import { useState, useEffect } from 'react';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';

export type ClassificacaoServico = 'Excelente' | 'Relevante' | 'Adequado' | 'Inadequado';

export interface NovoRegistoHistorico {
  funcionarioId: string;
  nomeFuncionario: string;
  carreira: string;
  ano: number;
  pontuacaoChefe: number;
  pontuacaoCCA: number;
  classificacaoServico: ClassificacaoServico;
}

interface HistoricoAddModalProps {
  initialData?: NovoRegistoHistorico;
  isEdit?: boolean;
  onSave: (data: NovoRegistoHistorico) => Promise<void>;
  onClose: () => void;
}

const classificacoes: ClassificacaoServico[] = ['Excelente', 'Relevante', 'Adequado', 'Inadequado'];

const classColors: Record<ClassificacaoServico, string> = {
  Excelente:  'bg-emerald-700 text-white border-emerald-700',
  Relevante:  'bg-teal-600 text-white border-teal-600',
  Adequado:   'bg-amber-600 text-white border-amber-600',
  Inadequado: 'bg-red-600 text-white border-red-600',
};

const currentYear = new Date().getFullYear();
const anos = Array.from({ length: currentYear - 2005 }, (_, i) => currentYear - 1 - i);

export default function HistoricoAddModal({ initialData, isEdit = false, onSave, onClose }: HistoricoAddModalProps) {
  const { funcionarios } = useFuncionarios();
  const { animating, handleClose } = useModalAnimation(onClose);
  const [saving, setSaving] = useState(false);

  const defaultForm: NovoRegistoHistorico = {
    funcionarioId: funcionarios[0]?.id ?? '',
    nomeFuncionario: funcionarios[0]?.nome ?? '',
    carreira: funcionarios[0]?.carreira ?? '',
    ano: currentYear - 1,
    pontuacaoChefe: 3.0,
    pontuacaoCCA: 3.0,
    classificacaoServico: 'Relevante',
  };

  const [form, setForm] = useState<NovoRegistoHistorico>(initialData ?? defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof NovoRegistoHistorico, string>>>({});

  useModalOverflow();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  }, [handleClose]);

  function handleFuncionarioChange(id: string) {
    const func = funcionarios.find((f) => f.id === id);
    setForm((prev) => ({
      ...prev,
      funcionarioId: id,
      nomeFuncionario: func?.nome ?? '',
      carreira: func?.carreira ?? '',
    }));
    setErrors((prev) => ({ ...prev, funcionarioId: undefined }));
  }

  function set<K extends keyof NovoRegistoHistorico>(key: K, value: NovoRegistoHistorico[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof NovoRegistoHistorico, string>> = {};
    if (!form.funcionarioId) newErrors.funcionarioId = 'Selecione um funcionário';
    if (!form.ano) newErrors.ano = 'Selecione o ano';
    if (form.pontuacaoChefe < 1 || form.pontuacaoChefe > 10) newErrors.pontuacaoChefe = 'Entre 1.0 e 10.0';
    if (form.pontuacaoCCA < 1 || form.pontuacaoCCA > 10) newErrors.pontuacaoCCA = 'Entre 1.0 e 10.0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-slate-800 text-lg">
              {isEdit ? 'Editar Registo Histórico' : 'Novo Registo Histórico'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {isEdit ? 'Altere os dados do registo de progressão' : 'Adicionar avaliação ao histórico de progressões'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          {/* Funcionário */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Funcionário *</label>
            <select
              value={form.funcionarioId}
              onChange={(e) => handleFuncionarioChange(e.target.value)}
              disabled={isEdit}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer disabled:bg-slate-50 disabled:text-slate-400 ${errors.funcionarioId ? 'border-red-300' : 'border-slate-200'}`}
            >
              <option value="">Selecionar funcionário...</option>
              {funcionarios.map((f) => (
                <option key={f.id} value={f.id}>#{f.id} — {f.nome}</option>
              ))}
            </select>
            {errors.funcionarioId && <p className="text-xs text-red-500 mt-1">{errors.funcionarioId}</p>}
          </div>

          {/* Ano */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Ano de Avaliação *</label>
            <select
              value={form.ano}
              onChange={(e) => set('ano', parseInt(e.target.value, 10))}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer ${errors.ano ? 'border-red-300' : 'border-slate-200'}`}
            >
              {anos.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            {errors.ano && <p className="text-xs text-red-500 mt-1">{errors.ano}</p>}
          </div>

          {/* Pontuações */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Pontuação Chefe *</label>
              <input
                type="number"
                min={1}
                max={10}
                step={0.1}
                value={form.pontuacaoChefe}
                onChange={(e) => set('pontuacaoChefe', parseFloat(e.target.value) || 1)}
                className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 ${errors.pontuacaoChefe ? 'border-red-300' : 'border-slate-200'}`}
              />
              {errors.pontuacaoChefe && <p className="text-xs text-red-500 mt-1">{errors.pontuacaoChefe}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Pontuação CCA *</label>
              <input
                type="number"
                min={1}
                max={10}
                step={0.1}
                value={form.pontuacaoCCA}
                onChange={(e) => set('pontuacaoCCA', parseFloat(e.target.value) || 1)}
                className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 ${errors.pontuacaoCCA ? 'border-red-300' : 'border-slate-200'}`}
              />
              {errors.pontuacaoCCA && <p className="text-xs text-red-500 mt-1">{errors.pontuacaoCCA}</p>}
            </div>
          </div>

          {/* Classificação de Serviço */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Classificação de Serviço *</label>
            <div className="flex flex-wrap gap-2">
              {classificacoes.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('classificacaoServico', c)}
                  className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-full border transition-colors cursor-pointer ${
                    form.classificacaoServico === c
                      ? classColors[c]
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="whitespace-nowrap px-5 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                </div>
                A guardar...
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${isEdit ? 'ri-save-line' : 'ri-add-line'} text-sm`} />
                </div>
                {isEdit ? 'Guardar Alterações' : 'Adicionar Registo'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
